/**
 * Sanitização de HTML na renderização.
 *
 * Comentário e descrição de chamado são gravados como HTML (editor rico) e
 * exibidos com `v-html`. O backend já aplica allowlist na gravação; aqui vai a
 * segunda barreira, usando o parser do próprio navegador — o que evita as
 * armadilhas de sanitizar HTML na base do regex.
 *
 * A lista abaixo acompanha o que o MiraiEditor (Tiptap) produz.
 */

const TAGS_PERMITIDAS = new Set([
  "P", "BR", "SPAN", "DIV",
  "STRONG", "B", "EM", "I", "U", "S", "STRIKE", "DEL", "INS", "MARK", "SUB", "SUP", "SMALL",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "UL", "OL", "LI",
  "BLOCKQUOTE", "PRE", "CODE", "HR",
  "A", "IMG", "FIGURE", "FIGCAPTION",
  "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TH", "TD", "COLGROUP", "COL",
]);

const ATRIBUTOS_GLOBAIS = new Set(["class", "style", "title", "dir"]);

const ATRIBUTOS_POR_TAG = {
  A: new Set(["href", "target", "rel"]),
  IMG: new Set(["src", "alt", "width", "height", "data-size", "data-align"]),
  FIGURE: new Set(["data-size", "data-align"]),
  OL: new Set(["start", "type", "data-list-style"]),
  UL: new Set(["data-list-style"]),
  LI: new Set(["data-checked", "data-type"]),
  TD: new Set(["colspan", "rowspan", "colwidth"]),
  TH: new Set(["colspan", "rowspan", "colwidth"]),
  COL: new Set(["width", "span"]),
  CODE: new Set(["data-language"]),
  PRE: new Set(["data-language"]),
  DIV: new Set(["data-type", "data-callout"]),
  SPAN: new Set(["data-type"]),
};

const PROTOCOLOS_PERMITIDOS = ["http:", "https:", "mailto:", "tel:", "data:image/"];

const CSS_PROIBIDO = /(expression|behavior|javascript\s*:|vbscript\s*:|@import|binding\s*:|url\s*\()/i;

function urlEhSegura(valor = "") {
  const url = String(valor).trim();
  if (!url) return false;
  if (/^[/#.?]/.test(url)) return true;
  const normalizada = url.replace(/[\s -]/g, "").toLowerCase();
  return PROTOCOLOS_PERMITIDOS.some((protocolo) => normalizada.startsWith(protocolo));
}

function limparAtributos(el) {
  const permitidosDaTag = ATRIBUTOS_POR_TAG[el.tagName];

  Array.from(el.attributes).forEach((attr) => {
    const nome = String(attr.name || "").toLowerCase();
    const valor = String(attr.value || "");

    const permitido = ATRIBUTOS_GLOBAIS.has(nome) || (permitidosDaTag && permitidosDaTag.has(nome));
    if (!permitido) {
      el.removeAttribute(attr.name);
      return;
    }

    if (nome === "style") {
      const limpo = valor
        .split(";")
        .map((declaracao) => declaracao.trim())
        .filter((declaracao) => declaracao && !CSS_PROIBIDO.test(declaracao))
        .join("; ");
      if (limpo) el.setAttribute("style", limpo);
      else el.removeAttribute("style");
      return;
    }

    if ((nome === "href" || nome === "src") && !urlEhSegura(valor)) {
      el.removeAttribute(attr.name);
    }
  });

  if (el.tagName === "A" && el.getAttribute("href")) {
    el.setAttribute("rel", "noopener noreferrer");
    el.setAttribute("target", "_blank");
  }
}

/**
 * Aplica a allowlist. Tag fora da lista é desembrulhada (o texto interno fica);
 * tag perigosa some com o conteúdo.
 */
export function sanitizarHtml(html = "") {
  const entrada = String(html || "");
  if (!entrada) return "";

  const doc = new DOMParser().parseFromString(entrada, "text/html");

  doc.querySelectorAll("script, style, iframe, object, embed, noscript, template, svg, math, form, input, button, select, textarea, link, meta, base")
    .forEach((el) => el.remove());

  // Percorre de trás para frente: desembrulhar um nó não invalida os índices
  // dos que ainda não foram visitados.
  const todos = Array.from(doc.body.querySelectorAll("*"));
  for (let i = todos.length - 1; i >= 0; i -= 1) {
    const el = todos[i];
    if (!TAGS_PERMITIDAS.has(el.tagName)) {
      el.replaceWith(...Array.from(el.childNodes));
      continue; // eslint-disable-line no-continue
    }
    limparAtributos(el);
  }

  return doc.body.innerHTML;
}

/** Texto plano do HTML — para checagens e resumos. */
export function htmlParaTexto(html = "") {
  const entrada = String(html || "");
  if (!entrada) return "";
  const doc = new DOMParser().parseFromString(entrada, "text/html");
  return String(doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

/**
 * O editor rico devolve "<p></p>" quando está vazio, então não dá para
 * validar por `trim().length`. Há conteúdo quando existe texto ou imagem.
 */
export function htmlTemConteudo(html = "") {
  const entrada = String(html || "");
  if (!entrada.trim()) return false;
  if (/<\s*img\b/i.test(entrada)) return true;
  return htmlParaTexto(entrada).length > 0;
}

export default { sanitizarHtml, htmlParaTexto, htmlTemConteudo };
